import React, { useState, useEffect } from 'react';
import Card from 'components/card';
import { BallTriangle } from 'react-loader-spinner';
import testService from 'ApiService/Teacher/TestService';
import toast from 'react-hot-toast';

const FailedStudentsTable = ({ classData, onAssignTest }) => {
  const [loading, setLoading] = useState(false);
  const [failedStudents, setFailedStudents] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    if (classData.courseId && classData.batchId) fetchFailedStudents();
    else setFailedStudents([]);
  }, [classData.courseId, classData.batchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFailedStudents = async () => {
    try {
      setLoading(true);
      const response = await testService.getAllFailedStudents({
        courseId: classData.courseId,
        batchId: classData.batchId,
      });

      const students = response?.data?.failedStudents || [];
      setFailedStudents(students);

      // Show info only if no students found
      if (students.length === 0) {
        toast('No failed students found for this course/batch.');
      }
    } catch (err) {
      console.error(err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load failed students';
      toast.error(errorMessage);
      setFailedStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (studentId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [studentId]: !prev[studentId],
    }));
  };

  // Group failed tests by student
  const studentsArray = Object.values(
    (failedStudents || []).reduce((acc, test) => {
      const studentId = test.studentId._id;
      if (!acc[studentId])
        acc[studentId] = { studentInfo: test.studentId, tests: [] };
      acc[studentId].tests.push(test);
      return acc;
    }, {})
  );

  if (loading)
    return (
      <div className="flex h-96 w-full items-center justify-center">
        <BallTriangle color="#5431ed" />
      </div>
    );

  if (!studentsArray.length)
    return (
      <div className="py-6 text-center text-gray-600 dark:text-gray-300">
        No failed students found.
      </div>
    );

  return (
    <div className="w-full overflow-x-auto">
      <Card extra="p-6">
        <h4 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white">
          Failed Students
        </h4>
        <table className="w-full border-collapse overflow-hidden rounded-lg shadow-sm">
          <thead className="bg-gray-100 dark:bg-navy-700">
            <tr className="text-left text-sm uppercase tracking-wide text-gray-600 dark:text-gray-300">
              <th className="border-b px-4 py-3">Name</th>
              <th className="border-b px-4 py-3">Email</th>
              <th className="border-b px-4 py-3 text-center">Failed Tests</th>
              <th className="border-b px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>

          <tbody className="text-sm text-gray-700 dark:text-gray-200">
            {studentsArray.map(({ studentInfo, tests }) => (
              <React.Fragment key={studentInfo._id}>
                <tr
                  onClick={() => toggleRow(studentInfo._id)}
                  className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-navy-800"
                >
                  <td className="border-b px-4 py-3 font-medium">
                    {studentInfo.name}
                  </td>
                  <td className="border-b px-4 py-3">{studentInfo.email}</td>
                  <td className="border-b px-4 py-3 text-center">
                    {tests.length}
                  </td>
                  <td
                    className="border-b px-4 py-3 text-center"
                    onClick={(e) => e.stopPropagation()} // Prevent row toggle when button clicked
                  >
                    <button
                      onClick={() => onAssignTest?.(studentInfo)}
                      className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow-md transition hover:bg-indigo-700"
                    >
                      Assign Test
                    </button>
                  </td>
                </tr>

                {expandedRows[studentInfo._id] && (
                  <tr>
                    <td
                      colSpan={4}
                      className="border-b bg-gray-50 p-4 dark:bg-navy-800"
                    >
                      <div className="space-y-3">
                        <h5 className="mb-2 font-semibold text-gray-800 dark:text-white">
                          Failed Test Details
                        </h5>
                        {tests.map((test) => (
                          <div
                            key={test._id}
                            className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-navy-600 dark:bg-navy-700"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                              <p className="font-medium text-gray-800 dark:text-gray-100">
                                {test.testId?.title || 'Untitled Test'}
                              </p>
                              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 sm:mt-0">
                                Score:{' '}
                                <span className="font-semibold">
                                  {test.score}
                                </span>{' '}
                                / {test.testId?.totalMarks}
                              </p>
                            </div>
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                              Type: {test.testId?.type?.toUpperCase() || 'N/A'}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Attempted On:{' '}
                              {new Date(test.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default FailedStudentsTable;
